import { ref, computed } from 'vue'
import Swal from 'sweetalert2'
import type { WaTemplate } from '@/types/templates'
import { listTemplates, cpTemplateToWaTemplate } from '@/services/wootdashTemplates'
import { sendTemplateInConversation } from '@/services/wootdashConversations'
import { CP_TEMPLATE_PAYLOAD_VARS } from '@/utils/templateVars'

// Irmão WootDash-nativo de useWhatsappTemplates.ts — mesma lógica de preview/
// substituição de variável (pura, sem I/O), mas busca/envio trocam a chamada direta à
// Graph API da Meta (token exposto no bundle) por wootdashTemplates.ts (Fase 1, já
// migrado) + sendTemplateInConversation (novo — persiste Message e notifica em tempo
// real, diferente do envio em lote que a tela de Templates usa).
export interface UseWhatsappTemplatesCpOptions {
  getToken: () => string
  getConversationId: () => string | null
  isWindowClosed?: () => boolean
  onSent?: () => void
}

export function useWhatsappTemplatesCp(options: UseWhatsappTemplatesCpOptions) {
  const tplOpen       = ref(false)
  const tplsLoading    = ref(false)
  const tplsAll        = ref<WaTemplate[]>([])
  const tplsApproved   = computed(() => tplsAll.value.filter(t => t.status === 'APPROVED'))
  const tplSel         = ref<WaTemplate | null>(null)
  const tplVarNums     = ref<number[]>([])
  const tplVars        = ref<Record<number, string>>({})
  const tplVarMapping  = ref<Record<number, string>>({})
  const tplImgUrl      = ref('')
  const tplImgErr      = ref(false)
  const tplSending     = ref(false)
  const tplTime        = new Date().getHours().toString().padStart(2, '0') + ':' + new Date().getMinutes().toString().padStart(2, '0')

  const tplSendBlocked = computed(() => false)
  const tplSendBlockedReason = computed(() => '')

  const tplHasImg    = computed(() => !!tplSel.value?.components.some((c: any) => c.type === 'HEADER' && c.format === 'IMAGE'))
  const tplHeaderTxt = computed(() => tplSel.value?.components.find((c: any) => c.type === 'HEADER' && c.format === 'TEXT')?.text || '')
  const tplFooterTxt = computed(() => tplSel.value?.components.find((c: any) => c.type === 'FOOTER')?.text || '')
  const tplButtons   = computed(() => tplSel.value?.components.find((c: any) => c.type === 'BUTTONS')?.buttons || [])
  const tplBodyRaw   = computed(() => tplSel.value?.components.find((c: any) => c.type === 'BODY')?.text || '')
  const tplPreviewHtml = computed(() => {
    let t = tplBodyRaw.value
    for (const n of tplVarNums.value) {
      if (tplVarMapping.value[n]) {
        t = t.replace(new RegExp(`\\{\\{${n}\\}\\}`, 'g'), `<mark class="var-highlight">${mappedLabel(n)}</mark>`)
        continue
      }
      const val = tplVars.value[n]
      const repl = val ? `<mark class="var-highlight">${val}</mark>` : `<mark class="var-highlight">{{${n}}}</mark>`
      t = t.replace(new RegExp(`\\{\\{${n}\\}\\}`, 'g'), repl)
    }
    return t.replace(/\n/g, '<br/>')
  })

  function mappedLabel(n: number): string {
    return CP_TEMPLATE_PAYLOAD_VARS.find(v => v.key === tplVarMapping.value[n])?.label || ''
  }

  function openTplModal() {
    tplOpen.value    = true
    tplSel.value     = null
    tplVarNums.value = []
    tplVars.value    = {}
    tplVarMapping.value = {}
    tplImgUrl.value  = ''
    tplImgErr.value  = false
    tplSending.value = false
    if (!tplsAll.value.length) fetchTpls()
  }

  function closeTplModal() { tplOpen.value = false }

  async function fetchTpls() {
    tplsLoading.value = true
    try {
      const raw = await listTemplates(options.getToken())
      tplsAll.value = raw.map(cpTemplateToWaTemplate)
    } catch {
      tplsAll.value = []
    } finally {
      tplsLoading.value = false
    }
  }

  function selectTpl(t: WaTemplate) {
    tplSel.value = t
    const bodyRaw = t.components.find((c: any) => c.type === 'BODY')?.text || ''
    tplVarNums.value = [...new Set([...(bodyRaw.matchAll(/\{\{(\d+)\}\}/g))].map((m: any) => Number(m[1])))].sort((a, b) => a - b)
    tplVars.value = {}
    try { tplVarMapping.value = JSON.parse(t.variableMappingJson || '{}') } catch { tplVarMapping.value = {} }
    tplImgErr.value = false
    const hdr = t.components.find((c: any) => c.type === 'HEADER')
    tplImgUrl.value = hdr?.example?.header_handle?.[0] || ''
  }

  function setTplVar(n: number, val: string) { tplVars.value = { ...tplVars.value, [n]: val } }

  async function sendTpl() {
    if (!tplSel.value || tplSending.value) return
    const conversationId = options.getConversationId()
    if (!conversationId) return
    tplSending.value = true
    try {
      const manualValues: Record<number, string> = {}
      for (const n of tplVarNums.value) {
        if (!tplVarMapping.value[n]) manualValues[n] = tplVars.value[n] || ''
      }
      const ok = await sendTemplateInConversation(
        options.getToken(), conversationId, tplSel.value.id,
        Object.keys(manualValues).length ? manualValues : undefined,
        tplHasImg.value && tplImgUrl.value ? tplImgUrl.value : undefined,
      )
      if (ok) {
        closeTplModal()
        options.onSent?.()
      } else {
        Swal.fire({ title: 'Erro ao enviar', text: 'Não foi possível enviar o template.', icon: 'error' })
      }
    } finally {
      tplSending.value = false
    }
  }

  return {
    tplOpen, tplsLoading, tplsAll, tplsApproved, tplSel, tplVarNums, tplVars, tplVarMapping, tplImgUrl, tplImgErr, tplSending, tplTime,
    tplHasImg, tplHeaderTxt, tplFooterTxt, tplButtons, tplBodyRaw, tplPreviewHtml,
    tplSendBlocked, tplSendBlockedReason,
    openTplModal, closeTplModal, fetchTpls, selectTpl, setTplVar, sendTpl, mappedLabel,
  }
}
